import type { CorrectionOverride, LegacyCollectionResult } from '../types'

/**
 * Faithful port of the pre-V2 ("legacy") Patient Responsibility card.
 *
 * Renders the flat `action`-shape collection result that the V2 PaymentOption
 * rewrite replaced. Kept self-contained (its own formatters + a minimal view of
 * the legacy rules-engine output) so historical / rolled-back-version docs
 * render without resurrecting the old schema modules. MedicalSlideout reaches
 * this only when a check carries `legacyCollectionResult` — i.e. no V2/new-shape
 * result exists for it. The surrounding accessories (AI thought process,
 * deductible accordion, referral banner) are reused from the current slideout.
 */

const STC_LABELS: Record<string, string> = {
  '30': 'Health Benefit Plan Coverage',
  '33': 'Chiropractic',
  '47': 'Hospital',
  '86': 'Emergency Services',
  '88': 'Pharmacy',
  '96': 'Professional (Physician) Visit - Specialist',
  '98': 'Professional (Physician) Visit - Office',
  'AL': 'Vision (Optometry)',
  'MH': 'Mental Health',
}

function stcLabel(stc: string): string {
  return `STC ${stc} ${STC_LABELS[stc] ?? ''}`.trim()
}

function formatCurrency(amount: number | null, decimals = 0): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

function obligationTypeLabel(action: string): string {
  if (action.includes('deductible') && action.includes('copay')) return 'deductible + copay'
  if (action.includes('deductible')) return 'applied to deductible'
  if (action.includes('coinsurance')) return 'coinsurance estimate'
  return 'copay'
}

const NEEDS_REVIEW_ACTIONS = new Set([
  'needs_review', 'verify_with_payer', 'check_capitation_roster', 'hold_for_auth',
])

interface Alternative {
  label: string
  description: string
}

function buildAlternatives(cr: LegacyCollectionResult): Alternative[] {
  const alts: Alternative[] = []
  const re = cr.rulesEngineOutput
  if (!re) return alts
  if (re.networkTiers) {
    for (const tier of re.networkTiers) {
      alts.push({
        label: `$${tier.amount} Copay`,
        description: `In-network · ${tier.networkTier ?? 'Standard'}`,
      })
    }
  }
  if (re.coinsurance && re.isOutOfNetwork) {
    const pct = re.coinsurance.percent >= 1 ? re.coinsurance.percent : re.coinsurance.percent * 100
    alts.push({ label: `${Math.round(pct)}% Coinsurance`, description: 'Out-of-network provider' })
  }
  return alts
}

function formatCorrectedValue(override: CorrectionOverride): string {
  if (override.selectedType === 'copay' && override.selectedAmount !== null) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(override.selectedAmount)
  }
  if (override.selectedType === 'coinsurance' && override.selectedPercent !== null) {
    return `${override.selectedPercent}%`
  }
  return '-'
}

function formatOriginalValue(override: CorrectionOverride): string {
  if (override.originalAmountRange && override.originalAmountRange.length >= 2) {
    return `$${override.originalAmountRange[0]} – $${override.originalAmountRange[override.originalAmountRange.length - 1]}`
  }
  if (override.originalAmount !== null) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(override.originalAmount)
  }
  return '-'
}

export default function LegacyPatientResponsibility({ cr, onViewBenefitDetail, override, onStartCorrection, onRevertCorrection, canCorrect }: {
  cr: LegacyCollectionResult
  onViewBenefitDetail: () => void
  override?: CorrectionOverride | null
  onStartCorrection?: () => void
  onRevertCorrection?: () => void
  canCorrect: boolean
}) {
  if (override) {
    const typeLabel = override.selectedType === 'copay' ? 'Co-payment' : 'Co-insurance'
    const networkLabel = override.selectedNetwork === 'in-network' ? 'In-Network' : 'Out-of-Network'
    return (
      <div className="patient-responsibility patient-responsibility--overridden">
        <div className="patient-responsibility__header">PATIENT RESPONSIBILITY</div>
        <div className="patient-responsibility__amount-row">
          <span className="patient-responsibility__amount">{formatCorrectedValue(override)}</span>
          <span className="patient-responsibility__type">{typeLabel} · {networkLabel}</span>
        </div>
        <table className="patient-responsibility__override-table">
          <tbody>
            <tr>
              <td className="patient-responsibility__override-label">Source</td>
              <td>STC {override.selectedStc} {override.selectedStcLabel}</td>
            </tr>
            <tr>
              <td className="patient-responsibility__override-label">Original AI value</td>
              <td><s>{formatOriginalValue(override)}</s></td>
            </tr>
            <tr>
              <td className="patient-responsibility__override-label">Reason</td>
              <td>{override.explanation}</td>
            </tr>
          </tbody>
        </table>
        <div className="patient-responsibility__override-footer">
          <button className="patient-responsibility__revert" onClick={onRevertCorrection}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Revert to AI suggestion
          </button>
          <span className="patient-responsibility__override-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
            Manually overridden
          </span>
        </div>
      </div>
    )
  }

  const isRange = cr.action === 'collect_copay_range' || (cr.amountRange !== null && cr.amountRange.length >= 2)
  const needsReview = NEEDS_REVIEW_ACTIONS.has(cr.action) || cr.confidence < 0.5
  const alternatives = isRange ? buildAlternatives(cr) : []

  // Dual-network display
  const re = cr.rulesEngineOutput
  const inAmount = cr.amount ?? re?.recommendedAmount ?? null
  const oonAction = cr.oonAction ?? re?.oonRecommendedAction ?? null
  const oonAmount = cr.oonAmount ?? re?.oonRecommendedAmount ?? null
  const oonCoinsurancePct = cr.oonCoinsurancePercent ?? re?.oonCoinsurance?.percent ?? null
  const hasDualNetwork = oonAction != null

  const inStc = cr.amountStc ?? re?.copay?.stcMatched ?? null
  const oonStc = cr.oonAmountStc ?? re?.oonCopay?.stcMatched ?? null

  return (
    <div className="patient-responsibility">
      <div className="patient-responsibility__header">
        PATIENT RESPONSIBILITY
      </div>
      {needsReview && !isRange && !hasDualNetwork ? (
        <div className="patient-responsibility__empty">
          <p className="patient-responsibility__empty-title">Unable to determine patient responsibility</p>
          <p className="patient-responsibility__empty-text">{cr.staffSummary || 'Review eligibility benefits'}</p>
        </div>
      ) : hasDualNetwork ? (
        <>
          <div className="patient-responsibility__dual">
            <div className="patient-responsibility__dual-col">
              <span className="patient-responsibility__dual-label">In-Network</span>
              <span className="patient-responsibility__dual-amount">{inAmount != null ? formatCurrency(inAmount) : `${Math.round((cr.coinsurancePercent ?? 0) * 100)}%`}</span>
              <span className="patient-responsibility__dual-type">{obligationTypeLabel(cr.action)}</span>
              {inStc && <span className="patient-responsibility__stc">{stcLabel(inStc)}</span>}
            </div>
            <div className="patient-responsibility__dual-divider" />
            <div className="patient-responsibility__dual-col">
              <span className="patient-responsibility__dual-label">Out-of-Network</span>
              <span className="patient-responsibility__dual-amount">{oonAmount != null ? formatCurrency(oonAmount) : oonCoinsurancePct != null ? `${Math.round(oonCoinsurancePct * 100)}%` : '—'}</span>
              <span className="patient-responsibility__dual-type">{obligationTypeLabel(oonAction!)}</span>
              {oonStc && <span className="patient-responsibility__stc">{stcLabel(oonStc)}</span>}
            </div>
          </div>
          <p className="patient-responsibility__context">{cr.staffSummary}</p>
        </>
      ) : isRange && cr.amountRange && cr.amountRange.length >= 2 ? (
        <>
          <div className="patient-responsibility__amount-row">
            <span className="patient-responsibility__amount">
              ${cr.amountRange[0]} – ${cr.amountRange[cr.amountRange.length - 1]}
            </span>
            <span className="patient-responsibility__type">estimated</span>
          </div>
          <p className="patient-responsibility__context">{cr.staffSummary}</p>
          {alternatives.length > 0 && (
            <div className="alt-candidates">
              {alternatives.map((alt, i) => (
                <button key={i} className="alt-candidate" onClick={onViewBenefitDetail}>
                  <div className="alt-candidate__info">
                    <span className="alt-candidate__amount">{alt.label}</span>
                    <span className="alt-candidate__desc">{alt.description}</span>
                  </div>
                  <svg className="alt-candidate__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </>
      ) : cr.amount !== null ? (
        <>
          <div className="patient-responsibility__amount-row">
            <span className="patient-responsibility__amount">{formatCurrency(cr.amount)}</span>
            <span className="patient-responsibility__type">{obligationTypeLabel(cr.action)}{inStc ? ` · ${stcLabel(inStc)}` : ''}</span>
          </div>
          <p className="patient-responsibility__context">{cr.staffSummary}</p>
        </>
      ) : (
        <div className="patient-responsibility__empty">
          <p className="patient-responsibility__empty-title">Unable to determine patient responsibility</p>
          <p className="patient-responsibility__empty-text">{cr.staffSummary || 'Review eligibility benefits'}</p>
        </div>
      )}

      {canCorrect && (
        <button type="button" className="patient-responsibility__flag-cta" onClick={onStartCorrection}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          <span>Flag as incorrect</span>
        </button>
      )}

      <div className="patient-responsibility__badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
        Generated by VitalRev AI
      </div>
    </div>
  )
}
